import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { Box, Paper, Stack, IconButton, CircularProgress, Fade, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import DefaultContext from "./auth/Context";
import SecureImage from "./SecureImage";

const ImageViewer = ({
  src,
  alt = "Image Viewer",
  size = "FULL",          // Kích thước ảnh yêu cầu server trả về (viewer luôn cần bản chất lượng cao)
  onLoad,
  onError,
  onClose,                // Có truyền => hiện nút Đóng ở góc trên phải + bắt phím ESC
  onDownload,             // Có truyền => hiện nút Tải xuống trên thanh công cụ
  sx = {}
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false); // Quản lý trạng thái lỗi
  const [isShareSupported, setIsShareSupported] = useState(false);
  
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0, rotation: 0 });
  const context = useContext(DefaultContext);
  
  // Dùng transformRef để tránh lỗi lấy sai giá trị cũ (stale state) khi kéo thả
  const transformRef = useRef(transform);
  useEffect(() => {
    transformRef.current = transform;
  }, [transform]);

  const isDragging = useRef(false);
  const isPinching = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const imageContainerRef = useRef(null);
  const lastTapTime = useRef(0);
  const lastDist = useRef(null);
  const lastPinchEndTime = useRef(0);

  // Reset lại trạng thái khi thay đổi src ảnh
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setTransform({ scale: 1, x: 0, y: 0, rotation: 0 });
  }, [src]);

  // Kiểm tra hỗ trợ Share khi component mount
  useEffect(() => {
    setIsShareSupported(typeof navigator !== 'undefined' && !!navigator.share);
  }, []);

  // Cho phép nhấn ESC để đóng khi viewer được dùng trong lightbox/modal
  useEffect(() => {
    if (!onClose) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose(e);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // --- HANDLER XỬ LÝ SỰ CỐ TẢI ÁNH (CỐT LÕI FIX LỖI XOAY VÔ TẬN) ---
  const handleImageLoad = (e) => {
    setIsLoading(false);
    setHasError(false);
    if (onLoad) onLoad(e);
  };

  const handleImageError = (err) => {
    setIsLoading(false); // Đóng ngay icon xoay tròn
    setHasError(true);   // Đánh dấu ảnh hỏng
    if (onError) onError(err);
  };

  // --- ZOOM LOGIC ---
  const zoom = useCallback((factor, clientX, clientY) => {
    if (!imageContainerRef.current) return;
    const rect = imageContainerRef.current.getBoundingClientRect();

    setTransform(prev => {
      const newScale = Math.min(Math.max(prev.scale * factor, 0.5), 10);
      const scaleFactor = newScale / prev.scale;

      let newX = prev.x;
      let newY = prev.y;

      if (clientX !== undefined && clientY !== undefined) {
        const offsetX = clientX - rect.left - rect.width / 2;
        const offsetY = clientY - rect.top - rect.height / 2;
        newX -= offsetX * (scaleFactor - 1);
        newY -= offsetY * (scaleFactor - 1);
      }

      if (newScale <= 1 && prev.rotation === 0) {
        return { ...prev, scale: newScale, x: 0, y: 0 };
      }
      return { ...prev, scale: newScale, x: newX, y: newY };
    });
  }, []);

  const handleZoomIn = () => zoom(1.3);
  const handleZoomOut = () => zoom(1 / 1.3);

  const resetState = () => {
    setTransform({ scale: 1, x: 0, y: 0, rotation: 0 });
  };

  const handleRotateClockwise = () => {
    setTransform(prev => ({ scale: 1, x: 0, y: 0, rotation: (prev.rotation + 90) % 360 }));
  };

  const handleRotateCounterClockwise = () => {
    setTransform(prev => ({ scale: 1, x: 0, y: 0, rotation: ((prev.rotation - 90) % 360 + 360) % 360 }));
  };

  // --- SHARE LOGIC ---
  const handleShare = async () => {
    if (navigator.share) {
      if(!context.config?.public_token) return context.alert("Không thể chia sẻ file này");
      let shareUrl = src;
      try {
        const urlObj = new URL(src, window.location.origin);
        urlObj.searchParams.set('access_token', context.config?.public_token);
        shareUrl = urlObj.toString();
      } catch (error) {
        return context.alert("Lỗi khi parse URL để share:", error);
      }

      try {
        await navigator.share({
          title: alt,
          url: shareUrl
        });
      } catch (error) {
        console.log("Hủy share hoặc lỗi:", error);
      }
    }
  };

  // --- DRAG / PAN ---
  const getEventCoords = (e) =>
    e.touches
      ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
      : { x: e.clientX, y: e.clientY };

  const onDragStart = (e) => {
    if (hasError || isPinching.current || (e.touches && e.touches.length > 1)) return;
    
    const curr = transformRef.current;
    if (curr.scale <= 1 && curr.rotation === 0) return;
    
    e.preventDefault();
    isDragging.current = true;
    const { x, y } = getEventCoords(e);
    startPos.current = { x: x - curr.x, y: y - curr.y };
  };

  const onDragMove = (e) => {
    if (!isDragging.current || isPinching.current || (e.touches && e.touches.length > 1)) return;
    e.preventDefault();
    const { x, y } = getEventCoords(e);
    
    setTransform(prev => ({
      ...prev,
      x: x - startPos.current.x,
      y: y - startPos.current.y
    }));
  };

  const onDragEnd = (e) => {
    isDragging.current = false;
    
    setTransform(prev => {
      if (prev.scale <= 1 && prev.rotation === 0) return { ...prev, x: 0, y: 0 };
      return prev;
    });

    const now = new Date().getTime();
    if (now - lastPinchEndTime.current < 350) return;

    if (e && e.changedTouches && e.changedTouches.length === 1 && !isPinching.current) {
      const tapLength = now - lastTapTime.current;
      if (tapLength < 300 && tapLength > 0) {
        setTransform(prev => {
          if (prev.scale > 1) {
            return { scale: 1, x: 0, y: 0, rotation: prev.rotation };
          } else {
            const touch = e.changedTouches[0];
            requestAnimationFrame(() => zoom(2.5, touch.clientX, touch.clientY));
            return prev; 
          }
        });
      }
      lastTapTime.current = now;
    }
  };

  // --- WHEEL ZOOM (DESKTOP) ---
  const onWheel = (e) => {
    if (hasError) return;
    e.preventDefault();
    const factor = e.deltaY > 0 ? (1 / 1.25) : 1.25;
    zoom(factor, e.clientX, e.clientY);
  };

  // --- PINCH ZOOM NATIVE MOBILE ---
  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container || hasError) return;

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        isPinching.current = true;
        isDragging.current = false;
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastDist.current = Math.sqrt(dx * dx + dy * dy);
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2 && isPinching.current) {
        e.preventDefault(); 
        
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (lastDist.current) {
          const factor = dist / lastDist.current;
          const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
          zoom(factor, midX, midY);
        }
        lastDist.current = dist;
      }
    };

    const handleTouchEnd = (e) => {
      if (e.touches.length < 2) {
        if (isPinching.current) {
          lastPinchEndTime.current = new Date().getTime();
        }
        isPinching.current = false;
        lastDist.current = null;
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [zoom, hasError]);

  return (
    <Box
      ref={imageContainerRef}
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        background: '#1a1a1a',
        cursor: isDragging.current ? 'grabbing' : (transform.scale > 1 ? 'grab' : 'default'),
        touchAction: 'none',
        ...sx
      }}
      onMouseDown={onDragStart}
      onMouseMove={onDragMove}
      onMouseUp={onDragEnd}
      onMouseLeave={onDragEnd}
      onTouchStart={onDragStart}
      onTouchMove={onDragMove}
      onTouchEnd={onDragEnd}
      onWheel={onWheel}
    >
      {/* 0. NÚT ĐÓNG (chỉ hiện khi viewer được nhúng trong lightbox/modal) */}
      {onClose && (
        <Tooltip title="Đóng (ESC)">
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 'calc(12px + env(safe-area-inset-top))',
              right: 12,
              zIndex: 20,
              color: '#fff',
              backgroundColor: 'rgba(0,0,0,0.45)',
              backdropFilter: 'blur(6px)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* 1. CHỈ HIỆN ICON XOAY KHI ĐANG LOADING BÌNH THƯỜNG */}
      {isLoading && !hasError && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5, color: 'grey.300' }}>
          <CircularProgress color="inherit" size={40} />
        </Box>
      )}

      {/* 2. CHỈ HIỆN CÁC NÚT ZOOM/ROTATE KHI ÁNH KHÔNG BỊ LỖI */}
      {!hasError && (
        <Paper
          elevation={6}
          sx={{
            position: 'absolute',
            bottom: 'calc(16px + env(safe-area-inset-bottom))',
            left: '50%',
            transform: 'translateX(-50%)',
            borderRadius: 8, 
            zIndex: 10,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Stack direction="row" spacing={0.5} sx={{ p: 1 }}>
            <Tooltip title="Phóng to"><IconButton size="small" onClick={handleZoomIn}><ZoomInIcon /></IconButton></Tooltip>
            <Tooltip title="Thu nhỏ"><IconButton size="small" onClick={handleZoomOut}><ZoomOutIcon /></IconButton></Tooltip>
            
            <Box sx={{ width: 1, height: 24, bgcolor: 'grey.300', mx: 0.5, alignSelf: 'center' }} />

            <Tooltip title="Xoay trái"><IconButton size="small" onClick={handleRotateCounterClockwise}><RotateLeftIcon /></IconButton></Tooltip>
            <Tooltip title="Xoay phải"><IconButton size="small" onClick={handleRotateClockwise}><RotateRightIcon /></IconButton></Tooltip>
            <Tooltip title="Đặt lại"><IconButton size="small" onClick={resetState}><RestartAltIcon /></IconButton></Tooltip>

            {onDownload && (
              <>
                <Box sx={{ width: 1, height: 24, bgcolor: 'grey.300', mx: 0.5, alignSelf: 'center' }} />
                <Tooltip title="Tải xuống">
                  <IconButton size="small" onClick={onDownload}><DownloadIcon /></IconButton>
                </Tooltip>
              </>
            )}

            {isShareSupported && (
              <>
                <Box sx={{ width: 1, height: 24, bgcolor: 'grey.300', mx: 0.5, alignSelf: 'center' }} />
                <Tooltip title="Chia sẻ">
                  <IconButton size="small" onClick={handleShare} color="primary">
                    <ShareIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        </Paper>
      )}

      {/* 3. HIỂN THỊ COMPONENT SECUREIMAGE */}
      <Fade in={!isLoading || hasError} timeout={400}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: hasError ? 'none' : `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale}) rotate(${transform.rotation}deg)`,
            transition: (isDragging.current || isPinching.current) 
              ? 'none' 
              : 'transform 0.1s cubic-bezier(0.2, 0.9, 0.3, 1)',
          }}
        >
          <Box
            component={SecureImage}
            src={src}
            alt={alt}
            size={size}
            onLoad={handleImageLoad}
            onError={handleImageError}
            sx={{
              maxWidth: '100%', maxHeight: '100%', display: 'block',
              objectFit: 'contain', userSelect: 'none', 
              pointerEvents: hasError ? 'auto' : 'none', // Nếu bị lỗi thì bật click để bấm nút Download
              imageRendering: 'high-quality', WebkitUserDrag: 'none',
            }}
          />
        </Box>
      </Fade>
    </Box>
  );
};

export default ImageViewer;